import { formatBytes, formatDate, formatUZS, type HostingPlan } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, HardDrive, Pencil, Plus, RefreshCw, Settings, Trash2 } from "lucide-react";
import { useState } from "react";

import { TrendChart } from "@/components/charts/TrendChart";
import { StorageCard } from "@/components/settings/StorageCard";
import { Button } from "@/components/ui/button";
import { Badge, Card, PageHeader, Spinner } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useDebounced } from "@/lib/hooks";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

import { ExpandDialog } from "./ExpandDialog";
import { ExpansionProgress } from "./ExpansionProgress";
import { ConfigDialog, PaymentDialog, PlanDialog } from "./HostingDialogs";
import { BackupsCard, SystemCard } from "./OpsCards";

const GB = 1024 ** 3;
const gb = (bytes: number) => Math.round((bytes / GB) * 100) / 100;

// Kalitlar literal: i18n qamrov testi dinamik kalitlarni ko'rmaydi
const KINDS = [
  ["photo", "server.kind_photo"],
  ["video", "server.kind_video"],
  ["scheme", "server.kind_scheme"],
  ["pdf", "server.kind_pdf"],
  ["catalog", "server.kind_catalog"],
  ["database", "server.kind_database"],
  ["backups", "server.kind_backups"],
  ["other", "server.kind_other"],
] as const;
const LEVELS = {
  info: "server.level_info",
  warning: "server.level_warning",
  critical: "server.level_critical",
} as const;
const LEVEL_TONE = { info: "info", warning: "warning", critical: "danger" } as const;
const COLOR_CLASS = {
  green: "text-success",
  yellow: "text-warning",
  red: "text-danger",
  unknown: "text-muted-foreground",
} as const;

function Big({
  label,
  value,
  sub,
  className,
}: {
  label: string;
  value: string;
  sub?: string;
  className?: string;
}) {
  return (
    <Card>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={cn("mt-1 text-2xl font-bold", className)}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function Bars({
  rows,
  empty,
}: {
  rows: { key: string; label: string; bytes: number }[];
  empty?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.bytes));
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <li key={r.key} className="grid grid-cols-[9rem_1fr_6rem] items-center gap-3 text-sm">
          <span className="truncate">{r.label}</span>
          <span className="h-2.5 rounded-full bg-muted" aria-hidden>
            <span
              className="block h-full rounded-full bg-primary"
              style={{ width: `${(r.bytes / max) * 100}%` }}
            />
          </span>
          <span className="text-right font-semibold">{formatBytes(r.bytes)}</span>
        </li>
      ))}
    </ul>
  );
}

function Calculator() {
  const { t } = useT();
  const [addGb, setAddGb] = useState("");
  const [budget, setBudget] = useState("");
  const a = useDebounced(addGb);
  const b = useDebounced(budget);
  const addN = Number(a) >= 1 ? Math.floor(Number(a)) : undefined;
  const budN = Number(b) >= 1 ? Math.floor(Number(b)) : undefined;
  const res = useQuery({
    queryKey: ["hosting", "calc", addN, budN],
    enabled: !!(addN || budN),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/hosting/calculator/", {
          params: { query: { add_gb: addN, budget_uzs: budN } },
        }),
      ),
  });
  const add = res.data?.add;
  const bud = res.data?.budget;
  const lasts = (months: number | null | undefined) =>
    months == null
      ? t("server.calc_lasts_unknown")
      : t("server.calc_lasts", { n: String(months).replace(".", ",") });
  return (
    <Card>
      <h2 className="mb-3 font-bold">{t("server.calc")}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          {t("server.calc_add")}
          <Input type="number" min={1} value={addGb} onChange={(e) => setAddGb(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          {t("server.calc_budget")}
          <Input type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)} />
        </label>
      </div>
      <div className="mt-3 space-y-3 text-sm" aria-live="polite">
        {add &&
          (add.available ? (
            <div data-testid="calc-add">
              <div className="font-semibold">
                {add.rate_missing
                  ? t("server.rate_missing")
                  : t("server.calc_add_result", {
                      gb: String(add.add_gb),
                      cost: formatUZS(add.extra_monthly_uzs ?? 0),
                    })}
              </div>
              <div className="text-muted-foreground">
                {t("server.calc_to", { gb: String(add.new_capacity_gb) })}
                {add.note ? ` · ${add.note}` : ""}
              </div>
              <div className="text-muted-foreground">{lasts(add.months)}</div>
            </div>
          ) : (
            <p data-testid="calc-add">{t("server.calc_unavailable")}</p>
          ))}
        {bud &&
          (bud.rate_missing ? (
            <p>{t("server.rate_missing")}</p>
          ) : bud.add_gb > 0 ? (
            <div data-testid="calc-budget">
              <div className="font-semibold">
                {t("server.calc_budget_result", {
                  budget: formatUZS(bud.budget_uzs),
                  gb: String(bud.add_gb),
                })}
              </div>
              <div className="text-muted-foreground">{lasts(bud.months)}</div>
            </div>
          ) : (
            <p data-testid="calc-budget">{t("server.calc_none")}</p>
          ))}
      </div>
    </Card>
  );
}

/** Faqat super_admin: haqiqiy disk, o'sish prognozi, xarajat, kalkulyator, to'lovlar, ogohlantirishlar. */
export function ServerPage() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [cfgOpen, setCfgOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [planEdit, setPlanEdit] = useState<HostingPlan | null>(null);
  const [planDel, setPlanDel] = useState<HostingPlan | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);

  const overview = useQuery({
    queryKey: ["hosting", "overview"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/overview/")),
  });
  const config = useQuery({
    queryKey: ["hosting", "config"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/config/")),
  });
  const plans = useQuery({
    queryKey: ["hosting", "plans"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/plans/")),
  });
  const payments = useQuery({
    queryKey: ["hosting", "payments"],
    queryFn: async () => unwrap(await api.GET("/api/hosting/payments/")),
  });
  const alerts = useQuery({
    queryKey: ["hosting", "alerts"],
    queryFn: async () =>
      unwrap(await api.GET("/api/notifications/", { params: { query: { open: "1" } } })),
  });
  const reload = () => void qc.invalidateQueries({ queryKey: ["hosting"] });

  const refresh = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/hosting/refresh/")),
    onSuccess: () => {
      toast.success(t("server.refresh_started"));
      window.setTimeout(reload, 4000);
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });
  const markRead = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST("/api/notifications/{id}/read/", { params: { path: { id } } })),
    onSuccess: reload,
  });
  const delPlan = useMutation({
    mutationFn: async (p: HostingPlan) => {
      const res = await api.DELETE("/api/hosting/plans/{id}/", { params: { path: { id: p.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setPlanDel(null);
      reload();
    },
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  const o = overview.data;
  if (overview.isError)
    return (
      <p role="alert" className="text-danger">
        {t("server.error")}
      </p>
    );
  if (!o) return <Spinner className="mx-auto my-10 size-8" />;

  const g = o.growth;
  const manual = o.provider.key === "manual";
  const fullIn =
    g.days_until_full != null
      ? t("server.days", { n: String(g.days_until_full) })
      : g.enough_data
        ? t("server.not_growing")
        : "—";
  const history = o.history.map((h) => ({ date: h.date, value: gb(h.used_bytes) }));
  const forecast = g.forecast.map((p) => ({
    date: p.date,
    value: gb(p.used_bytes),
    low: gb(p.low),
    high: gb(p.high),
  }));
  const kinds = KINDS.map(([k, label]) => ({
    key: k,
    label: t(label),
    bytes: o.breakdown.by_kind[k] ?? 0,
  })).filter((r) => r.bytes > 0);
  const nextDays = o.next_payment.days_left;
  const open = alerts.data?.results ?? [];

  return (
    <>
      <PageHeader
        title={t("server.title")}
        actions={
          <div className="flex flex-wrap gap-2">
            {o.provider.can_auto_expand && (
              <Button onClick={() => setExpandOpen(true)}>
                <HardDrive className="size-4" /> {t("server.expand")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setCfgOpen(true)} disabled={!config.data}>
              <Settings className="size-4" /> {t("server.settings")}
            </Button>
            <Button
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
              title={t("server.refresh_hint")}
            >
              <RefreshCw className={cn("size-4", refresh.isPending && "animate-spin")} />{" "}
              {refresh.isPending ? t("server.refreshing") : t("server.refresh")}
            </Button>
          </div>
        }
      />

      <ExpansionProgress enabled />
      <Card className="mb-4">
        <h2 className="mb-2 font-bold">{t("server.alerts")}</h2>
        {open.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("server.alerts_none")}</p>
        ) : (
          <ul className="space-y-2" data-testid="alerts">
            {open.map((n) => (
              <li key={n.id} className="flex flex-wrap items-center gap-3 text-sm">
                <Badge tone={LEVEL_TONE[n.level]}>{t(LEVELS[n.level])}</Badge>
                <span
                  className={cn("font-semibold", n.read_at && "font-normal text-muted-foreground")}
                >
                  {n.title}
                </span>
                <span className="text-muted-foreground">{n.body}</span>
                {!n.read_at && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => markRead.mutate(n.id)}
                  >
                    {t("server.mark_read")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="mb-4">
        <StorageCard />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="big-numbers">
        <Big label={t("server.total")} value={formatBytes(o.disk.total)} />
        <Big
          label={t("server.used")}
          value={formatBytes(o.disk.used)}
          sub={t("server.percent", { n: String(o.disk.percent).replace(".", ",") })}
        />
        <Big label={t("server.free")} value={formatBytes(o.disk.free)} />
        <Big
          label={t("server.full_in")}
          value={fullIn}
          className={COLOR_CLASS[g.color]}
          sub={
            g.full_on
              ? t("server.full_on", { date: formatDate(g.full_on) })
              : g.enough_data
                ? undefined
                : t("server.unknown_growth", {
                    needed: String(g.days_needed),
                    have: String(g.days_have),
                  })
          }
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {o.disk.source === "host-agent" ? t("server.source_agent") : t("server.source_local")}
      </p>

      <Card className="mt-4">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-bold">{t("server.chart")}</h2>
          {g.enough_data && g.growth_per_month != null && g.in_3_months && (
            <div className="text-sm text-muted-foreground" data-testid="growth-summary">
              {t("server.growth_month")}: <b>{formatBytes(Math.max(g.growth_per_month, 0))}</b> ·{" "}
              {t("server.in_3m")}: <b>{formatBytes(g.in_3_months.used_bytes)}</b> (
              {t("server.range", {
                low: formatBytes(g.in_3_months.low),
                high: formatBytes(g.in_3_months.high),
              })}
              )
            </div>
          )}
        </div>
        {history.length > 1 || forecast.length > 0 ? (
          <TrendChart
            history={history}
            forecast={forecast}
            label={t("server.chart_label")}
            format={(n) => `${n} GB`}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("server.unknown_growth", {
              needed: String(g.days_needed),
              have: String(g.days_have),
            })}
          </p>
        )}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="font-bold">{t("server.breakdown")}</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            {o.breakdown.date
              ? t("server.breakdown_date", { date: formatDate(o.breakdown.date) })
              : ""}
          </p>
          <Bars rows={kinds} />
        </Card>
        <div className="grid gap-4">
          <Card>
            <h2 className="mb-3 font-bold">{t("server.by_company")}</h2>
            <Bars
              rows={o.breakdown.by_company.map((c) => ({
                key: c.id,
                label: c.name,
                bytes: c.bytes,
              }))}
              empty={t("server.nobody")}
            />
          </Card>
          <Card>
            <h2 className="mb-3 font-bold">{t("server.by_user")}</h2>
            <Bars
              rows={o.breakdown.by_user.map((c) => ({ key: c.id, label: c.name, bytes: c.bytes }))}
              empty={t("server.nobody")}
            />
          </Card>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card data-testid="costs">
          <h2 className="mb-3 font-bold">{t("server.costs")}</h2>
          {o.costs.rate_missing && (
            <p className="mb-2 text-sm text-warning">{t("server.rate_missing")}</p>
          )}
          <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
            {(
              [
                ["server.cost_server", o.costs.server_uzs],
                ["server.cost_disk", o.costs.disk_uzs],
                ["server.cost_backup", o.costs.backup_uzs],
              ] as const
            ).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground">{t(k)}</dt>
                <dd className="text-right">{v == null ? "—" : formatUZS(v)}</dd>
              </div>
            ))}
            <dt className="mt-1 font-bold">{t("server.cost_total")}</dt>
            <dd className="mt-1 text-right font-bold">
              {o.costs.total_uzs == null ? "—" : formatUZS(o.costs.total_uzs)}
            </dd>
          </dl>
          <div className="mt-3 flex flex-wrap gap-x-4 text-xs text-muted-foreground">
            {o.fx.map((f) => (
              <span key={f.currency}>
                {t("server.fx")} {f.currency}:{" "}
                {f.rate ? `${formatUZS(f.rate, "")}`.trim() : t("server.fx_none")}
                {f.rate_date ? ` (${formatDate(f.rate_date)})` : ""}
                {f.stale && f.rate ? ` · ${t("server.fx_stale")}` : ""}
              </span>
            ))}
          </div>
        </Card>
        <Card data-testid="next-payment">
          <h2 className="mb-2 font-bold">{t("server.next_payment")}</h2>
          <div className="text-2xl font-bold">
            {o.next_payment.date ? formatDate(o.next_payment.date) : t("server.no_payment_date")}
          </div>
          {nextDays != null && (
            <div
              className={cn(
                "text-sm",
                nextDays <= 3
                  ? "text-danger"
                  : nextDays <= 14
                    ? "text-warning"
                    : "text-muted-foreground",
              )}
            >
              {nextDays < 0 ? t("server.overdue") : t("server.days_left", { n: String(nextDays) })}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {o.provider.billing_url && (
              <a
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-4 text-sm font-semibold hover:bg-muted"
                href={o.provider.billing_url}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="size-4" />{" "}
                {manual ? t("server.pay") : t("server.invoices")}
              </a>
            )}
            <Button variant="outline" onClick={() => setPayOpen(true)}>
              <Plus className="size-4" /> {t("server.payment_add")}
            </Button>
          </div>
          <h3 className="mb-1 mt-4 text-sm font-bold">{t("server.payments")}</h3>
          {(payments.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("server.payments_empty")}</p>
          ) : (
            <ul className="divide-y divide-border text-sm" data-testid="payments">
              {payments.data!.slice(0, 8).map((p) => (
                <li key={p.id} className="flex justify-between gap-3 py-1.5">
                  <span>
                    {formatDate(p.paid_on)}
                    {p.note ? ` · ${p.note}` : ""}
                  </span>
                  <span className="whitespace-nowrap">
                    {p.currency === "UZS" ? formatUZS(p.amount) : `${p.amount} ${p.currency}`} →{" "}
                    {formatDate(p.covers_until)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <SystemCard />
        <BackupsCard />
      </div>

      <div className="mt-4">
        <Calculator />
      </div>

      {manual && (
        <Card className="mt-4" data-testid="plans">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="font-bold">{t("server.plans")}</h2>
            <Button
              variant="outline"
              onClick={() => {
                setPlanEdit(null);
                setPlanOpen(true);
              }}
            >
              <Plus className="size-4" /> {t("server.plan_add")}
            </Button>
          </div>
          {(plans.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("server.plans_empty")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {plans.data!.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <span className="font-semibold">{p.name}</span>
                  <span>{p.size_gb} GB</span>
                  <span>
                    {p.currency === "UZS" ? formatUZS(p.price) : `${p.price} ${p.currency}`} /{" "}
                    {p.period === "yearly" ? t("server.period_yearly") : t("server.period_monthly")}
                  </span>
                  <span className="text-muted-foreground">
                    {t("server.plan_checked_on", { date: formatDate(p.checked_on) })}
                  </span>
                  {p.stale && <Badge tone="warning">{t("server.plan_stale")}</Badge>}
                  <span className="ml-auto flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`${t("common.edit")}: ${p.name}`}
                      onClick={() => {
                        setPlanEdit(p);
                        setPlanOpen(true);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`${t("common.delete")}: ${p.name}`}
                      onClick={() => setPlanDel(p)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {config.data && (
        <ConfigDialog
          open={cfgOpen}
          onOpenChange={setCfgOpen}
          config={config.data}
          plans={plans.data ?? []}
        />
      )}
      <PlanDialog open={planOpen} onOpenChange={setPlanOpen} plan={planEdit} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} />
      <ExpandDialog open={expandOpen} onOpenChange={setExpandOpen} />
      <ConfirmDialog
        open={!!planDel}
        onOpenChange={(v) => !v && setPlanDel(null)}
        title={t("common.delete")}
        message={planDel ? t("server.confirm_plan_delete", { name: planDel.name }) : ""}
        confirmLabel={t("common.delete")}
        pending={delPlan.isPending}
        onConfirm={() => planDel && delPlan.mutate(planDel)}
      />
    </>
  );
}
